/**
 * Onboarding wizard (full-screen). Shown until `profiles.onboarding_completed` is true.
 *
 * --- Existing users: run in Supabase SQL Editor (not executed by this app) ---
 * UPDATE profiles
 * SET onboarding_completed = true
 * WHERE business_name IS NOT NULL
 *   AND owner_name IS NOT NULL
 *   AND country IS NOT NULL
 *   AND industry IS NOT NULL;
 * ---------------------------------------------------------------------------
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Logo from './Logo';
import { Spinner } from './Spinner';
import { SearchableCountrySelect } from './SearchableCountrySelect';
import { COUNTRIES, type CountryOption } from '../data/countries';
import { INDUSTRIES } from '../data/industries';
import { PHONE_DIAL_CODES } from '../data/phoneDialCodes';
import { getComplianceFramework, getCurrencyForCountry } from '../lib/countryCompliance';
import { isValidDomain, normalizeDomain } from '../lib/domainUtils';
import { supabase } from '../lib/supabase';
import { addMonitoredAsset, checkAssetTrial, getAssetPrice } from '../server/api';
import { inputClass, labelClass } from '../pages/settings/styles';
import { useAuth } from '../contexts/AuthContext';

type MonitoredAssetRow = {
  asset_type: string;
  asset_value: string;
};

type AssetTrialStatus = {
  hasActiveTrial: boolean;
  isExpired: boolean;
  trialExpiresAt: string | null;
};

function resolveCountryOption(countryName: string, countryCode: string): CountryOption | null {
  const code = countryCode.trim().toUpperCase();
  if (code) {
    const byCode = COUNTRIES.find((c) => c.code === code);
    if (byCode) return byCode;
  }
  const name = countryName.trim();
  if (name) {
    const byName = COUNTRIES.find((c) => c.name === name);
    if (byName) return byName;
  }
  return null;
}

export type OnboardingCompleteInfo = {
  /** Non-fatal asset API errors during step 3 (wizard still completes). */
  assetWarnings?: string[];
};

type OnboardingWizardProps = {
  onComplete: (info?: OnboardingCompleteInfo) => Promise<void>;
};

type WizardStep = 1 | 2 | 3;

type AlertFreq = 'immediate' | 'daily' | 'weekly';

function parseWhatsappStored(full: string | null): { dial: string; national: string } {
  if (!full?.trim()) return { dial: '+1', national: '' };
  const s = full.trim();
  if (!s.startsWith('+')) return { dial: '+1', national: s.replace(/\D/g, '') };
  const sorted = [...PHONE_DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const { dial } of sorted) {
    if (s.startsWith(dial)) {
      return { dial, national: s.slice(dial.length).replace(/\D/g, '') };
    }
  }
  return { dial: '+1', national: s.replace(/\D/g, '') };
}

function buildE164(dial: string, nationalDigits: string): string {
  const d = nationalDigits.replace(/\D/g, '');
  return `${dial}${d}`;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function normalizeFrequency(raw: string | null | undefined): AlertFreq {
  const s = (raw ?? 'immediate').toLowerCase();
  if (s === 'daily' || s === 'weekly') return s;
  return 'immediate';
}

function WizardSwitch({
  checked,
  onChange,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  id: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${
        checked ? 'border-accent bg-accent/20' : 'border-border bg-navy'
      }`}
    >
      <span
        className={`h-6 w-6 rounded-full shadow transition-transform ${
          checked ? 'translate-x-5 bg-accent' : 'translate-x-0 bg-foreground'
        }`}
      />
    </button>
  );
}

function IconEnvelope({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconWhatsApp({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function IconBolt({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13.01 3h1l-1 7h3.51c.49 0 .49.33.26.66C15.96 12.66 14.5 15.5 11 21z" />
    </svg>
  );
}

function IconCalendar({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
    </svg>
  );
}

function IconChart({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M4 19V5M8 19v-6M12 19V9M16 19v-4M20 19v-9" strokeLinecap="round" />
    </svg>
  );
}

function StepProgress({
  step,
  labels,
}: {
  step: WizardStep;
  labels: readonly [string, string, string];
}) {
  const indices = [1, 2, 3] as const;

  const line = (leftDone: boolean) => (
    <div
      className="mx-0.5 min-h-[2px] min-w-[6px] flex-1 self-center rounded-full sm:mx-1 sm:min-w-[10px]"
      style={
        leftDone
          ? { backgroundColor: 'var(--accent-green)' }
          : { borderTop: '2px dashed var(--border-color)' }
      }
    />
  );

  const circle = (index: 1 | 2 | 3) => {
    const current = step === index;

    if (step > index) {
      return (
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--accent-green)' }}
          aria-hidden
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M3 8l3 3 7-7"
              stroke="var(--text-on-accent)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }

    if (current) {
      return (
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold"
          style={{ backgroundColor: 'var(--accent-green)', color: 'var(--text-on-accent)' }}
        >
          {index}
        </div>
      );
    }

    return (
      <div
        className="h-7 w-7 shrink-0 rounded-full border-2"
        style={{ borderColor: 'var(--border-color)' }}
        aria-hidden
      />
    );
  };

  const labelClassFor = (index: 1 | 2 | 3) => {
    const current = step === index;
    if (current) {
      return 'max-w-[5.5rem] text-center text-[10px] font-bold underline sm:max-w-none sm:text-xs md:text-sm';
    }
    return 'max-w-[5.5rem] text-center text-[10px] sm:max-w-none sm:text-xs md:text-sm';
  };

  const labelStyle = (index: 1 | 2 | 3): React.CSSProperties => {
    const current = step === index;
    if (current) return { color: 'var(--text-primary)' };
    return { color: 'var(--text-tertiary)' };
  };

  return (
    <div className="mb-6 w-full">
      <div className="flex w-full items-center justify-between gap-0">
        {indices.map((idx, i) => (
          <React.Fragment key={idx}>
            {i > 0 ? line(step >= idx) : null}
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 sm:gap-2">
              {circle(idx)}
              <span className={labelClassFor(idx)} style={labelStyle(idx)}>
                {labels[idx - 1]!}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function formatCompanySizeLabel(value: string | null | undefined): string {
  switch (value) {
    case '1':
      return 'Just me';
    case '2-10':
      return '2–10';
    case '11-50':
      return '11–50';
    case '51-200':
      return '51–200';
    case '200+':
      return '200+';
    default:
      return '—';
  }
}

export default function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { currentUser, userProfile } = useAuth();
  const userId = currentUser?.id;

  const [step, setStep] = useState<WizardStep>(1);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');

  const [emailAlerts, setEmailAlerts] = useState(true);
  const [whatsappAlerts, setWhatsappAlerts] = useState(false);
  const [dialCode, setDialCode] = useState(() => parseWhatsappStored(null).dial);
  const [phoneNational, setPhoneNational] = useState(() => parseWhatsappStored(null).national);
  const [alertFrequency, setAlertFrequency] = useState<AlertFreq>('immediate');
  const [channelError, setChannelError] = useState<string | null>(null);

  const [domainInput, setDomainInput] = useState('');
  const [emailMonitor, setEmailMonitor] = useState('');
  const [domainTouched, setDomainTouched] = useState(false);
  const [domainTrialStatus, setDomainTrialStatus] = useState<AssetTrialStatus | null>(null);
  const [emailTrialStatus, setEmailTrialStatus] = useState<AssetTrialStatus | null>(null);
  const [domainTrialChecking, setDomainTrialChecking] = useState(false);
  const [emailTrialChecking, setEmailTrialChecking] = useState(false);
  const [domainPrice, setDomainPrice] = useState<{ amount: number; currency: string } | null>(null);
  const [emailPrice, setEmailPrice] = useState<{ amount: number; currency: string } | null>(null);
  const [assetPricingLoading, setAssetPricingLoading] = useState(false);

  const [initialDomainNorm, setInitialDomainNorm] = useState<string | null>(null);
  const [initialEmailLower, setInitialEmailLower] = useState<string | null>(null);

  const hasPreFilled = useRef(false);
  const hasPreFilledAssets = useRef(false);

  const countryOption = useMemo(
    () => resolveCountryOption(country, countryCode),
    [country, countryCode]
  );

  const industryOptions = useMemo(() => {
    const base = [...INDUSTRIES] as string[];
    const p = industry.trim() || userProfile?.industry?.trim();
    if (p && !base.includes(p)) return [p, ...base];
    return base;
  }, [industry, userProfile?.industry]);

  useEffect(() => {
    hasPreFilled.current = false;
  }, [userProfile?.id]);

  useEffect(() => {
    if (!userProfile) return;
    if (hasPreFilled.current) return;
    hasPreFilled.current = true;

    if (userProfile.business_name) setBusinessName(userProfile.business_name);
    if (userProfile.owner_name) setOwnerName(userProfile.owner_name);
    if (userProfile.account_type === 'personal' && !userProfile.business_name?.trim()) {
      setBusinessName(userProfile.owner_name?.trim() ?? '');
    }
    if (userProfile.country) setCountry(userProfile.country);
    if (userProfile.country_code) setCountryCode(userProfile.country_code);
    if (userProfile.industry) setIndustry(userProfile.industry);
    if (userProfile.region) setRegion(userProfile.region);
    setEmailAlerts(userProfile.email_alerts ?? true);
    setWhatsappAlerts(userProfile.whatsapp_alerts ?? false);
    const parsed = parseWhatsappStored(userProfile.whatsapp_number ?? null);
    setDialCode(parsed.dial);
    setPhoneNational(parsed.national);
    if (userProfile.alert_frequency) setAlertFrequency(normalizeFrequency(userProfile.alert_frequency));
  }, [userProfile]);

  useEffect(() => {
    hasPreFilledAssets.current = false;
  }, [currentUser?.id]);

  useEffect(() => {
    if (step !== 3) return;
    const uid = currentUser?.id;
    if (!uid) return;
    if (hasPreFilledAssets.current) return;
    hasPreFilledAssets.current = true;

    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('monitored_assets')
        .select('asset_type, asset_value')
        .eq('user_id', uid);

      if (cancelled) return;

      if (error || !data) {
        setInitialDomainNorm(null);
        setInitialEmailLower(null);
        return;
      }

      const rows = data as MonitoredAssetRow[];
      const domainRow = rows.find((r) => r.asset_type === 'domain');
      const emailRow = rows.find((r) => r.asset_type === 'email');

      if (domainRow?.asset_value) {
        setDomainInput(domainRow.asset_value.trim());
        const n = normalizeDomain(domainRow.asset_value);
        setInitialDomainNorm(n && isValidDomain(n) ? n : null);
      } else {
        setInitialDomainNorm(null);
      }

      if (emailRow?.asset_value) {
        const ev = emailRow.asset_value.trim();
        setEmailMonitor(ev);
        setInitialEmailLower(ev.toLowerCase());
      } else {
        setInitialEmailLower(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, currentUser]);

  useEffect(() => {
    if (step !== 3) return;
    const profileCountry = userProfile?.country?.trim() ?? '';
    const countryForPricing =
      profileCountry.length === 2
        ? profileCountry.toUpperCase()
        : (countryOption?.code ??
          COUNTRIES.find((c) => c.name.toLowerCase() === profileCountry.toLowerCase())?.code ??
          profileCountry.toUpperCase());
    if (!countryForPricing) return;

    let cancelled = false;
    void (async () => {
      setAssetPricingLoading(true);
      const [domainRes, emailRes] = await Promise.all([
        getAssetPrice('domain', countryForPricing, 'monthly'),
        getAssetPrice('email', countryForPricing, 'monthly'),
      ]);
      if (cancelled) return;
      setDomainPrice(domainRes.ok ? domainRes.price : null);
      setEmailPrice(emailRes.ok ? emailRes.price : null);
      setAssetPricingLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [step, userProfile?.country, countryOption?.code]);

  useEffect(() => {
    if (step !== 3) return;
    const norm = normalizeDomain(domainInput);
    if (!norm || !isValidDomain(norm)) {
      setDomainTrialStatus(null);
      return;
    }
    const domainUnchanged = initialDomainNorm !== null && norm === initialDomainNorm;
    if (domainUnchanged) return;

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      void (async () => {
        setDomainTrialChecking(true);
        const result = await checkAssetTrial('domain', norm);
        if (cancelled) return;
        if (result.ok) {
          setDomainTrialStatus({
            hasActiveTrial: result.hasActiveTrial,
            isExpired: result.isExpired,
            trialExpiresAt: result.trialExpiresAt,
          });
        } else {
          setDomainTrialStatus(null);
        }
        setDomainTrialChecking(false);
      })();
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [step, domainInput, initialDomainNorm]);

  useEffect(() => {
    if (step !== 3) return;
    const emailNormalized = emailMonitor.trim().toLowerCase();
    if (!emailNormalized || !isValidEmail(emailNormalized)) {
      setEmailTrialStatus(null);
      return;
    }
    const emailUnchanged = initialEmailLower !== null && emailNormalized === initialEmailLower;
    if (emailUnchanged) return;

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      void (async () => {
        setEmailTrialChecking(true);
        const result = await checkAssetTrial('email', emailNormalized);
        if (cancelled) return;
        if (result.ok) {
          setEmailTrialStatus({
            hasActiveTrial: result.hasActiveTrial,
            isExpired: result.isExpired,
            trialExpiresAt: result.trialExpiresAt,
          });
        } else {
          setEmailTrialStatus(null);
        }
        setEmailTrialChecking(false);
      })();
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [step, emailMonitor, initialEmailLower]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const complianceFromProfile = userProfile?.compliance_framework?.trim() ?? '';
  const complianceFromCountry =
    countryCode.trim().length > 0
      ? getComplianceFramework(countryCode)
      : countryOption
        ? getComplianceFramework(countryOption.code)
        : '';
  const complianceLabel = complianceFromProfile || complianceFromCountry || '';

  const isPersonalAccount = userProfile?.account_type === 'personal';
  const isBusinessAccount = userProfile?.account_type === 'business';
  const useStep1QuickConfirm = isPersonalAccount || isBusinessAccount;

  const wizardStepProgressLabels = useMemo(
    (): readonly [string, string, string] => [
      useStep1QuickConfirm
        ? isPersonalAccount
          ? 'Your details'
          : 'Confirm details'
        : 'Business Info',
      'Preferences',
      'Assets',
    ],
    [useStep1QuickConfirm, isPersonalAccount]
  );

  const wizardStepMainTitles = useMemo(
    (): readonly [string, string, string] => [
      useStep1QuickConfirm
        ? isPersonalAccount
          ? 'Confirm your details'
          : 'Confirm your business details'
        : 'Tell us about your business',
      'Set your preferences',
      'Monitor your assets',
    ],
    [useStep1QuickConfirm, isPersonalAccount]
  );

  const step1Valid = useMemo(() => {
    if (!useStep1QuickConfirm) {
      return (
        businessName.trim().length > 0 &&
        ownerName.trim().length > 0 &&
        countryOption !== null &&
        industry.trim().length > 0
      );
    }
    if (isPersonalAccount) {
      return ownerName.trim().length > 0 && countryOption !== null;
    }
    return (
      businessName.trim().length > 0 &&
      ownerName.trim().length > 0 &&
      countryOption !== null &&
      industry.trim().length > 0
    );
  }, [
    businessName,
    countryOption,
    industry,
    isPersonalAccount,
    ownerName,
    useStep1QuickConfirm,
  ]);

  const whatsappDigits = phoneNational.replace(/\D/g, '');
  const step2Valid =
    (emailAlerts || whatsappAlerts) &&
    (!whatsappAlerts || whatsappDigits.length >= 6);

  const normalizedDomain = useMemo(() => normalizeDomain(domainInput), [domainInput]);
  const domainHasError =
    domainInput.trim().length > 0 &&
    (!normalizedDomain || !isValidDomain(normalizedDomain) || /\s/.test(domainInput));
  const emailMonitorTrim = emailMonitor.trim();
  const emailHasError = emailMonitorTrim.length > 0 && !isValidEmail(emailMonitorTrim);
  const step3FinishEnabled =
    (!domainInput.trim() || (!domainHasError && isValidDomain(normalizedDomain))) &&
    (!emailMonitorTrim || !emailHasError);

  const showDomainAlreadyMonitored =
    initialDomainNorm !== null &&
    normalizedDomain.length > 0 &&
    isValidDomain(normalizedDomain) &&
    normalizedDomain === initialDomainNorm;

  const showEmailAlreadyMonitored =
    initialEmailLower !== null &&
    emailMonitorTrim.length > 0 &&
    isValidEmail(emailMonitorTrim) &&
    emailMonitorTrim.toLowerCase() === initialEmailLower;

  const formatMoney = useCallback((amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency || 'USD'}`;
    }
  }, []);

  const domainTrialMessage = useMemo(() => {
    if (domainTrialChecking) return 'Checking trial status...';
    if (!domainTrialStatus) return null;
    if (domainTrialStatus.isExpired) {
      return 'Trial expired for this asset. You will be billed immediately.';
    }
    if (domainTrialStatus.hasActiveTrial) {
      const expiresLabel = domainTrialStatus.trialExpiresAt
        ? new Date(domainTrialStatus.trialExpiresAt).toLocaleDateString()
        : 'soon';
      return `Trial active — expires ${expiresLabel}`;
    }
    return '30-day free trial available';
  }, [domainTrialChecking, domainTrialStatus]);

  const emailTrialMessage = useMemo(() => {
    if (emailTrialChecking) return 'Checking trial status...';
    if (!emailTrialStatus) return null;
    if (emailTrialStatus.isExpired) {
      return 'Trial expired for this asset. You will be billed immediately.';
    }
    if (emailTrialStatus.hasActiveTrial) {
      const expiresLabel = emailTrialStatus.trialExpiresAt
        ? new Date(emailTrialStatus.trialExpiresAt).toLocaleDateString()
        : 'soon';
      return `Trial active — expires ${expiresLabel}`;
    }
    return '30-day free trial available';
  }, [emailTrialChecking, emailTrialStatus]);

  const step3Estimate = useMemo(() => {
    const domainSelected =
      normalizedDomain.length > 0 &&
      isValidDomain(normalizedDomain) &&
      !showDomainAlreadyMonitored;
    const emailSelected =
      emailMonitorTrim.length > 0 &&
      isValidEmail(emailMonitorTrim) &&
      !showEmailAlreadyMonitored;

    let total = 0;
    let currency = domainPrice?.currency ?? emailPrice?.currency ?? 'USD';
    let selectedCount = 0;
    let trialEligibleCount = 0;

    if (domainSelected) {
      selectedCount += 1;
      total += domainPrice?.amount ?? 0;
      currency = domainPrice?.currency ?? currency;
      const available =
        domainTrialStatus?.hasActiveTrial === false &&
        domainTrialStatus?.isExpired === false &&
        domainTrialStatus?.trialExpiresAt === null;
      if (available) trialEligibleCount += 1;
    }

    if (emailSelected) {
      selectedCount += 1;
      total += emailPrice?.amount ?? 0;
      currency = emailPrice?.currency ?? currency;
      const available =
        emailTrialStatus?.hasActiveTrial === false &&
        emailTrialStatus?.isExpired === false &&
        emailTrialStatus?.trialExpiresAt === null;
      if (available) trialEligibleCount += 1;
    }

    return { total, currency, selectedCount, trialEligibleCount };
  }, [
    domainPrice?.amount,
    domainPrice?.currency,
    domainTrialStatus?.hasActiveTrial,
    domainTrialStatus?.isExpired,
    domainTrialStatus?.trialExpiresAt,
    emailMonitorTrim,
    emailPrice?.amount,
    emailPrice?.currency,
    emailTrialStatus?.hasActiveTrial,
    emailTrialStatus?.isExpired,
    emailTrialStatus?.trialExpiresAt,
    normalizedDomain,
    showDomainAlreadyMonitored,
    showEmailAlreadyMonitored,
  ]);

  const setEmailAlertsSafe = useCallback(
    (next: boolean) => {
      if (!next && !whatsappAlerts) {
        setChannelError('At least one alert channel is required');
        return;
      }
      setChannelError(null);
      setEmailAlerts(next);
    },
    [whatsappAlerts]
  );

  const setWhatsappAlertsSafe = useCallback(
    (next: boolean) => {
      if (!next && !emailAlerts) {
        setChannelError('At least one alert channel is required');
        return;
      }
      setChannelError(null);
      setWhatsappAlerts(next);
    },
    [emailAlerts]
  );

  function handleDialChange(nextDial: string) {
    setDialCode(nextDial);
  }

  function handleNationalChange(nextNational: string) {
    setPhoneNational(nextNational);
  }

  async function saveStep1(): Promise<boolean> {
    if (!userId || !countryOption) return false;
    setSaveError(null);
    setSaving(true);
    const industryForSave =
      industry.trim() ||
      (userProfile?.account_type === 'personal' ? 'Individual' : '');
    const businessNameForSave =
      userProfile?.account_type === 'personal'
        ? businessName.trim() || ownerName.trim() || 'Personal'
        : businessName.trim();
    const { error } = await supabase
      .from('profiles')
      .update({
        business_name: businessNameForSave,
        owner_name: ownerName.trim(),
        country: countryOption.name,
        country_code: countryOption.code,
        region: region.trim() || null,
        industry: industryForSave,
        currency: getCurrencyForCountry(countryOption.code),
        compliance_framework: getComplianceFramework(countryOption.code),
      })
      .eq('id', userId);
    setSaving(false);
    if (error) {
      setSaveError(error.message || 'Could not save. Please try again.');
      return false;
    }
    return true;
  }

  async function saveStep2(): Promise<boolean> {
    if (!userId) return false;
    setSaveError(null);
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({
        email_alerts: emailAlerts,
        whatsapp_alerts: whatsappAlerts,
        whatsapp_number: whatsappAlerts ? buildE164(dialCode, phoneNational) : null,
        alert_frequency: alertFrequency,
      })
      .eq('id', userId);
    setSaving(false);
    if (error) {
      setSaveError(error.message || 'Could not save. Please try again.');
      return false;
    }
    return true;
  }

  async function handleNext() {
    if (step === 1) {
      const ok = await saveStep1();
      if (ok) setStep(2);
      return;
    }
    if (step === 2) {
      const ok = await saveStep2();
      if (ok) setStep(3);
    }
  }

  async function markOnboardingComplete(): Promise<boolean> {
    if (!userId) return false;
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_completed: true })
      .eq('id', userId);
    if (error) {
      setSaveError(error.message || 'Could not complete onboarding.');
      return false;
    }
    return true;
  }

  function formatAssetAddErrorMessage(raw: string): string {
    const lower = raw.toLowerCase();
    if (lower.includes('trial')) {
      return 'Your free trial for this asset type is already active. You can manage your assets from the Assets page.';
    }
    return raw;
  }

  async function handleFinishStep3(skipAssets: boolean) {
    if (!userId) return;
    setSaveError(null);
    setSaving(true);
    let collectedWarnings: string[] = [];
    try {
      if (!skipAssets) {
        const dTrim = domainInput.trim();
        const norm = dTrim ? normalizeDomain(domainInput.trim()) : '';
        if (dTrim && (!norm || !isValidDomain(norm))) {
          setSaveError('Please fix the domain field before continuing.');
          return;
        }
        if (emailMonitorTrim && !isValidEmail(emailMonitorTrim)) {
          setSaveError('Please fix the email field before continuing.');
          return;
        }

        const warnings: string[] = [];

        if (norm && isValidDomain(norm)) {
          const domainUnchanged = initialDomainNorm !== null && norm === initialDomainNorm;
          if (!domainUnchanged) {
            const dRes = await addMonitoredAsset('domain', norm);
            if (!dRes.ok) {
              warnings.push(`Domain: ${formatAssetAddErrorMessage(dRes.error)}`);
            }
          }
        }

        if (emailMonitorTrim && isValidEmail(emailMonitorTrim)) {
          const emLower = emailMonitorTrim.trim().toLowerCase();
          const emailUnchanged = initialEmailLower !== null && emLower === initialEmailLower;
          if (!emailUnchanged) {
            const eRes = await addMonitoredAsset('email', emLower);
            if (!eRes.ok) {
              warnings.push(`Email: ${formatAssetAddErrorMessage(eRes.error)}`);
            }
          }
        }

        collectedWarnings = warnings;
      }

      const ok = await markOnboardingComplete();
      if (!ok) return;
      await onComplete(
        collectedWarnings.length > 0 ? { assetWarnings: collectedWarnings } : undefined
      );
    } finally {
      setSaving(false);
    }
  }

  const panelStyle: React.CSSProperties = {
    width: 'min(calc(100% - 32px), 560px)',
    maxHeight: '90vh',
    borderRadius: 20,
    backgroundColor: 'var(--bg-card)',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.2)',
  };

  const freqCardStyle = (selected: boolean): React.CSSProperties =>
    selected
      ? {
          border: '2px solid var(--accent-green)',
          background: 'color-mix(in srgb, var(--accent-green) 8%, transparent)',
        }
      : {
          border: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-card)',
        };

  return (
    <div
      className="fixed inset-0 z-[280] flex items-start justify-center overflow-y-auto p-4 sm:items-center"
      style={{ backgroundColor: 'var(--modal-overlay-bg)' }}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full flex-col overflow-hidden"
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-wizard-title"
        aria-describedby="onboarding-wizard-step"
      >
        <header className="shrink-0 px-8 pb-0 pt-6">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>
          <StepProgress step={step} labels={wizardStepProgressLabels} />
          <h1
            id="onboarding-wizard-title"
            className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-lg font-semibold text-foreground sm:text-xl"
          >
            {step === 1 && useStep1QuickConfirm && isBusinessAccount ? (
              <>
                <svg
                  className="mr-2 inline-block h-5 w-5 shrink-0 text-accent"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{wizardStepMainTitles[step - 1]}</span>
              </>
            ) : (
              wizardStepMainTitles[step - 1]
            )}
          </h1>
          <p id="onboarding-wizard-step" className="mt-1 text-center text-sm text-muted">
            Step {step} of 3
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-8 py-6">
          <div className="w-full overflow-hidden">
            <div
              className="flex w-[300%] transition-transform duration-[250ms] [transition-timing-function:ease]"
              style={{ transform: `translateX(-${((step - 1) * 100) / 3}%)` }}
            >
              <section className="box-border w-1/3 min-w-0 shrink-0 pr-1">
                {useStep1QuickConfirm && isPersonalAccount ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted">
                      We pulled this from your signup. Add a region if you like, then continue.
                    </p>
                    <div className="rounded-xl border border-border bg-surface/60 p-4 text-sm">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Full name</p>
                          <p className="font-medium text-foreground">{ownerName || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Country</p>
                          <p className="font-medium text-foreground">{country || '—'}</p>
                        </div>
                        {userProfile?.city?.trim() ? (
                          <div>
                            <p className="mb-0.5 text-xs text-muted">City</p>
                            <p className="font-medium text-foreground">{userProfile.city}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="ob-region" className={labelClass}>
                        Region <span className="text-muted font-normal">(optional)</span>
                      </label>
                      <input
                        id="ob-region"
                        className={inputClass}
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        placeholder="e.g. Greater Accra"
                      />
                    </div>
                    {complianceLabel ? (
                      <p className="text-xs font-medium text-accent">Compliance: {complianceLabel}</p>
                    ) : null}
                  </div>
                ) : useStep1QuickConfirm && isBusinessAccount ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted">
                      Confirm the details you used at signup. Add an optional region if you want it on record.
                    </p>
                    <div className="rounded-xl border border-border bg-surface/60 p-4 text-sm">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Business name</p>
                          <p className="font-medium text-foreground">{businessName || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Your name</p>
                          <p className="font-medium text-foreground">{ownerName || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Country</p>
                          <p className="font-medium text-foreground">{country || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Industry</p>
                          <p className="font-medium text-foreground">{industry || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Company size</p>
                          <p className="font-medium text-foreground">
                            {formatCompanySizeLabel(userProfile?.company_size)}
                          </p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Your role</p>
                          <p className="font-medium text-foreground">{userProfile?.job_role?.trim() || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Phone</p>
                          <p className="font-medium text-foreground">{userProfile?.phone_number?.trim() || '—'}</p>
                        </div>
                        <div>
                          <p className="mb-0.5 text-xs text-muted">Website</p>
                          <p className="font-medium text-foreground">{userProfile?.website?.trim() || '—'}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="ob-region-biz" className={labelClass}>
                        Region <span className="text-muted font-normal">(optional)</span>
                      </label>
                      <input
                        id="ob-region-biz"
                        className={inputClass}
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        placeholder="e.g. Greater Accra, Lagos State"
                      />
                    </div>
                    {complianceLabel ? (
                      <p className="text-xs font-medium text-accent">Compliance: {complianceLabel}</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="ob-business" className={labelClass}>
                        Business Name<span className="text-danger">*</span>
                      </label>
                      <input
                        id="ob-business"
                        className={inputClass}
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        placeholder="e.g. Kofi's Tech Solutions"
                        autoComplete="organization"
                      />
                    </div>
                    <div>
                      <label htmlFor="ob-owner" className={labelClass}>
                        Owner Name<span className="text-danger">*</span>
                      </label>
                      <input
                        id="ob-owner"
                        className={inputClass}
                        value={ownerName}
                        onChange={(e) => setOwnerName(e.target.value)}
                        placeholder="Your full name"
                        autoComplete="name"
                      />
                    </div>
                    <div>
                      <span className={labelClass}>
                        Country<span className="text-danger">*</span>
                      </span>
                      <SearchableCountrySelect
                        id="ob-country"
                        countries={COUNTRIES}
                        value={countryOption}
                        onChange={(opt) => {
                          setCountry(opt?.name ?? '');
                          setCountryCode(opt?.code ?? '');
                        }}
                        placeholder="Search or select country"
                      />
                      {complianceLabel ? (
                        <p className="mt-2 text-xs font-medium text-accent">
                          Your compliance framework: {complianceLabel}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <label htmlFor="ob-industry" className={labelClass}>
                        Industry<span className="text-danger">*</span>
                      </label>
                      <select
                        id="ob-industry"
                        className={`${inputClass} appearance-none bg-navy`}
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                      >
                        <option value="">Select industry</option>
                        {industryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ob-region" className={labelClass}>
                        Region
                      </label>
                      <input
                        id="ob-region"
                        className={inputClass}
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        placeholder="e.g. Greater Accra, Lagos State"
                      />
                    </div>
                  </div>
                )}
              </section>

              <section className="box-border w-1/3 min-w-0 shrink-0 px-1">
                <p className={labelClass}>
                  Alert Channel<span className="text-danger">*</span>
                </p>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <div
                    className="flex flex-1 flex-col rounded-xl border border-border p-4"
                    style={{ backgroundColor: 'var(--bg-surface)' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 gap-3">
                        <IconEnvelope className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
                        <div>
                          <p className="font-semibold text-foreground">Email Alerts</p>
                          <p className="mt-1 text-sm text-muted">Receive security alerts to your registered email</p>
                        </div>
                      </div>
                      <WizardSwitch
                        id="ob-email-alerts"
                        checked={emailAlerts}
                        onChange={setEmailAlertsSafe}
                      />
                    </div>
                  </div>
                  <div
                    className="flex flex-1 flex-col rounded-xl border border-border p-4"
                    style={{ backgroundColor: 'var(--bg-surface)' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 gap-3">
                        <IconWhatsApp className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
                        <div>
                          <p className="font-semibold text-foreground">WhatsApp Alerts</p>
                          <p className="mt-1 text-sm text-muted">Get instant alerts on WhatsApp</p>
                        </div>
                      </div>
                      <WizardSwitch
                        id="ob-wa-alerts"
                        checked={whatsappAlerts}
                        onChange={setWhatsappAlertsSafe}
                      />
                    </div>
                  </div>
                </div>

                {channelError ? (
                  <p className="mt-2 text-sm text-danger" role="alert">
                    {channelError}
                  </p>
                ) : null}

                {whatsappAlerts ? (
                  <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,11rem)_1fr]">
                    <div>
                      <label htmlFor="ob-wa-dial" className={labelClass}>
                        Dial code
                      </label>
                      <select
                        id="ob-wa-dial"
                        value={dialCode}
                        onChange={(e) => handleDialChange(e.target.value)}
                        className={`${inputClass} appearance-none bg-navy`}
                      >
                        {PHONE_DIAL_CODES.map((o) => (
                          <option key={`${o.dial}-${o.label}`} value={o.dial}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ob-wa-phone" className={labelClass}>
                        WhatsApp number
                      </label>
                      <input
                        id="ob-wa-phone"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="712 345 678"
                        value={phoneNational}
                        onChange={(e) => handleNationalChange(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>
                ) : null}

                <fieldset className="mt-8 border-0 p-0">
                  <legend className={`${labelClass} mb-3`}>
                    Alert Frequency<span className="text-danger">*</span>
                  </legend>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <button
                      type="button"
                      onClick={() => setAlertFrequency('immediate')}
                      className="flex flex-col items-center rounded-xl p-4 text-center transition"
                      style={freqCardStyle(alertFrequency === 'immediate')}
                    >
                      <IconBolt className="mb-2 h-8 w-8 text-accent" />
                      <span className="font-semibold text-foreground">Immediately</span>
                      <span className="mt-1 text-xs text-muted">Get alerted the moment a threat is detected</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAlertFrequency('daily')}
                      className="flex flex-col items-center rounded-xl p-4 text-center transition"
                      style={freqCardStyle(alertFrequency === 'daily')}
                    >
                      <IconCalendar className="mb-2 h-8 w-8 text-accent" />
                      <span className="font-semibold text-foreground">Daily Digest</span>
                      <span className="mt-1 text-xs text-muted">One summary email each day</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAlertFrequency('weekly')}
                      className="flex flex-col items-center rounded-xl p-4 text-center transition sm:col-span-1"
                      style={freqCardStyle(alertFrequency === 'weekly')}
                    >
                      <IconChart className="mb-2 h-8 w-8 text-accent" />
                      <span className="font-semibold text-foreground">Weekly Digest</span>
                      <span className="mt-1 text-xs text-muted">One summary email each week</span>
                    </button>
                  </div>
                </fieldset>
              </section>

              <section className="box-border w-1/3 min-w-0 shrink-0 px-0.5 pl-1">
                <h2 className="text-base font-semibold text-foreground">What would you like us to monitor?</h2>
                <p className="mt-1 text-sm text-muted">
                  You can add more anytime in Settings. Both fields are optional.
                </p>
                <div className="mt-6 space-y-5">
                  <div>
                    <label htmlFor="ob-domain" className={labelClass}>
                      Business Domain
                    </label>
                    <div className="relative">
                      <input
                        id="ob-domain"
                        className={`${inputClass} ${domainHasError ? 'border-danger' : ''} ${!domainHasError && normalizedDomain && isValidDomain(normalizedDomain) ? 'pr-10' : ''}`}
                        value={domainInput}
                        onChange={(e) => {
                          setDomainInput(e.target.value);
                          setDomainTouched(true);
                        }}
                        onBlur={() => setDomainTouched(true)}
                        placeholder="yourbusiness.com"
                        autoComplete="url"
                      />
                      {!domainHasError && normalizedDomain && isValidDomain(normalizedDomain) ? (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-accent" aria-hidden>
                          ✓
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-xs text-muted">
                      We&apos;ll monitor this domain for threats using AlienVault OTX
                    </p>
                    <p className="mt-1 text-xs font-medium text-foreground">
                      {assetPricingLoading
                        ? 'Loading price...'
                        : `Free for 30 days, then ${
                            domainPrice
                              ? `${formatMoney(domainPrice.amount, domainPrice.currency)}/mo`
                              : '$0/mo'
                          }`}
                    </p>
                    {showDomainAlreadyMonitored ? (
                      <p className="mt-1.5 text-xs font-medium text-accent">Already monitoring this domain</p>
                    ) : null}
                    {domainTrialMessage && !showDomainAlreadyMonitored ? (
                      <p className="mt-1 text-xs text-accent">{domainTrialMessage}</p>
                    ) : null}
                    {domainTouched && domainHasError ? (
                      <p className="mt-1 text-sm text-danger" role="alert">
                        Please enter a valid domain (e.g. yourbusiness.com)
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <label htmlFor="ob-email-monitor" className={labelClass}>
                      Email Address to Monitor
                    </label>
                    <input
                      id="ob-email-monitor"
                      type="email"
                      className={`${inputClass} ${emailHasError ? 'border-danger' : ''}`}
                      value={emailMonitor}
                      onChange={(e) => setEmailMonitor(e.target.value)}
                      placeholder="yourname@yourbusiness.com"
                      autoComplete="email"
                    />
                    <p className="mt-1.5 text-xs text-muted">
                      We&apos;ll check if this email has appeared in data breaches
                    </p>
                    <p className="mt-1 text-xs font-medium text-foreground">
                      {assetPricingLoading
                        ? 'Loading price...'
                        : `Free for 30 days, then ${
                            emailPrice
                              ? `${formatMoney(emailPrice.amount, emailPrice.currency)}/mo`
                              : '$0/mo'
                          }`}
                    </p>
                    {showEmailAlreadyMonitored ? (
                      <p className="mt-1.5 text-xs font-medium text-accent">Already monitoring this email</p>
                    ) : null}
                    {emailTrialMessage && !showEmailAlreadyMonitored ? (
                      <p className="mt-1 text-xs text-accent">{emailTrialMessage}</p>
                    ) : null}
                    {emailHasError ? (
                      <p className="mt-1 text-sm text-danger" role="alert">
                        Please enter a valid email address.
                      </p>
                    ) : null}
                  </div>
                  <div className="rounded-lg border border-border bg-surface/60 p-3">
                    <p className="text-xs text-muted">
                      {step3Estimate.selectedCount > 0 && step3Estimate.trialEligibleCount === step3Estimate.selectedCount
                        ? 'Free for 30 days'
                        : `Your estimated monthly cost after trial: ${formatMoney(step3Estimate.total, step3Estimate.currency)}/mo`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleFinishStep3(true)}
                    disabled={saving}
                    className="text-sm text-muted underline-offset-2 transition hover:text-foreground hover:underline disabled:opacity-50"
                  >
                    Skip for now
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>

        <footer
          className="shrink-0 border-t px-8 pb-6 pt-4"
          style={{ borderColor: 'var(--border-color)' }}
        >
          {saveError ? (
            <p className="mb-3 text-center text-sm text-danger" role="alert">
              {saveError}
            </p>
          ) : null}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {step > 1 ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setSaveError(null);
                    setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s));
                  }}
                  className="w-full rounded-lg border border-border px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-50 sm:w-auto"
                >
                  Back
                </button>
              ) : null}
            </div>
            <div className="flex justify-end">
              {step < 3 ? (
                <button
                  type="button"
                  disabled={saving || (step === 1 ? !step1Valid : !step2Valid)}
                  onClick={() => void handleNext()}
                  className="flex min-w-[140px] items-center justify-center gap-2 rounded-lg px-7 py-3 text-sm font-semibold text-cta-text transition disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent-green)' }}
                >
                  {saving ? <Spinner className="h-5 w-5 text-cta-text" /> : null}
                  Next
                </button>
              ) : step === 3 ? (
                <button
                  type="button"
                  disabled={saving || !step3FinishEnabled}
                  onClick={() => void handleFinishStep3(false)}
                  className="flex min-w-[140px] items-center justify-center gap-2 rounded-lg px-7 py-3 text-sm font-semibold text-cta-text transition disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent-green)' }}
                >
                  {saving ? <Spinner className="h-5 w-5 text-cta-text" /> : null}
                  Finish
                </button>
              ) : null}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
